import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import configData from '../../configure';
import './Faculty.css';
import { useAuth } from '../../AuthContext';
import { toast } from 'react-toastify';

function StaffBulkImport({ orgId: propOrgId }) {
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [importedStaff, setImportedStaff] = useState([]);
  const [importSummary, setImportSummary] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { axios, token, orgId: contextOrgId } = useAuth();
  const orgId = propOrgId || contextOrgId || location.state?.orgId;

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (
        selectedFile.type !== 'text/csv' &&
        !selectedFile.name.toLowerCase().endsWith('.csv')
      ) {
        toast.error('Please upload a valid CSV file.');
        setUploadStatus('error');
        return;
      }
      setFile(selectedFile);
      setUploadStatus('idle');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.warn('No file selected.');
      setUploadStatus('error');
      return;
    }
    if (!orgId) {
      toast.error('Organization ID is missing.');
      setUploadStatus('error');
      return;
    }

    setUploadStatus('uploading');
    setIsProcessing(true);
    setImportSummary(null);

    const fileReader = new FileReader();
    fileReader.onload = async (e) => {
      const csvText = e.target.result;
      const rows = csvText.split('\n').slice(1); // Skip header row

      const staffToProcess = [];
      const stats = {
        total: 0,
        success: 0,
        duplicates: 0,
        failed: 0,
        errors: []
      };

      rows.forEach((row, index) => {
        if (row.trim() === '') return;

        const values = row.split(',').map((v) => v.trim());
        if (values.length < 24) { // Keep existing check
          stats.failed++;
          stats.errors.push({
            row: index + 2,
            email: 'N/A',
            reason: `Insufficient columns (${values.length} found, minimum 24 required).`
          });
          return;
        }

        const [
          firstName,
          lastName,
          emailRaw,
          phoneNumber,
          gender,
          dob,
          placeOfBirth,
          bloodGroup,
          nationality,
          religion,
          category,
          currentAddress,
          currentAddress2,
          city,
          state,
          pincode,
          alternatePhoneNumber,
          employeeId,
          designation,
          department,
          joiningDate,
          staffContractType,
          responsibilities,
          identificationMark,
          drivingLicenseNumber,
          drivingLicenseValidity,
          drivingExperienceYears,
          lastFourDigitsOfAadhaarNumber,
        ] = values;

        let email = emailRaw ? String(emailRaw).trim() : '';
        if (!email && firstName && lastName) {
          const fn = String(firstName).toLowerCase().replace(/[^a-z0-9]/g, '');
          const ln = String(lastName).toLowerCase().replace(/[^a-z0-9]/g, '');
          email = `${fn}.${ln}@sevabharathi.com`;
        }

        // Basic validations
        if (!firstName || !lastName || !email || !employeeId) {
          stats.failed++;
          stats.errors.push({
            row: index + 2,
            email: email || 'Unknown',
            reason: "Missing required fields (firstName, lastName, email, or employeeId)."
          });
          return;
        }

        // Email validation
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          stats.failed++;
          stats.errors.push({
            row: index + 2,
            email: email,
            reason: "Invalid email format."
          });
          return;
        }

        staffToProcess.push({
          originalRow: index + 2,
          email,
          payload: {
            user: {
              creationTime: new Date().toISOString(),
              createdBy: 'admin',
              lastUpdatedTime: new Date().toISOString(),
              lastUpdatedBy: 'admin',
              orgId,
              userType: 'STAFF',
              roleId: 'ROLE_STAFF',
              userStatus: 'ACTIVE',
              email,
              phoneNumber,
              lastFourDigitsOfAadhaarNumber,
              firstName,
              lastName,
              dateOfBirth: dob ? new Date(dob).toISOString() : null,
              gender: gender ? gender.toUpperCase() : "UNKNOWN",
              placeOfBirth,
              bloodGroup,
              nationality,
              religion,
              category,
              addressDetails: [
                {
                  addressType: 'CURRENT',
                  addressLine1: currentAddress,
                  addressLine2: currentAddress2,
                  city,
                  state,
                  country: 'India',
                  pinCode: pincode,
                  alternatePhoneNumber,
                },
              ],
            },
            staffInfo: {
              creationTime: new Date().toISOString(),
              createdBy: 'admin',
              lastUpdatedTime: new Date().toISOString(),
              lastUpdatedBy: 'admin',
              employeeId,
              designation,
              department,
              joiningDate: joiningDate ? new Date(joiningDate).toISOString() : null,
              staffContractType: staffContractType ? staffContractType.toUpperCase() : "PERMANENT",
              responsibilities: responsibilities ? responsibilities.split(';').map((r) => r.trim()) : [],
              identificationMark,
              drivingLicenseNumber,
              drivingLicenseValidity: drivingLicenseValidity
                ? new Date(drivingLicenseValidity).toISOString()
                : null,
              drivingExperienceYears: drivingExperienceYears
                ? parseInt(drivingExperienceYears)
                : 0,
            },
            studentInfo: null,
            parentInfo: null,
            facultyInfo: null,
          }
        });
      });

      stats.total = staffToProcess.length + stats.failed;

      if (staffToProcess.length === 0 && stats.failed === 0) {
        toast.info('No valid staff data found in CSV.');
        setUploadStatus('error');
        setIsProcessing(false);
        return;
      }

      try {
        const registeredStaff = [];

        for (const item of staffToProcess) {
          const { payload, email, originalRow } = item;

          // Backend
          try {
            const response = await axios.post(
              `${configData.REGISTER_USER}`,
              payload,
              {
                headers: {
                  accept: 'application/json',
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`, // Assuming token is handled by interceptor or this manual header logic is correct
                },
              }
            );

            if (response.status === 200 || response.status === 201) {
              stats.success++;
              registeredStaff.push(response.data);

              // Send Email
              try {
                const newUserId = response.data?.userId || response.data?.user?.userId;
                if (newUserId && token) {
                  const emailData = {
                    subject: "Welcome to Egurukul - Your Account Details",
                    messageBody: `Welcome to Egurukul!\n\nYour account has been successfully created.\n\nLogin Details:\nUsername: ${email}\nPassword: egurukul@2026\n\nPlease login and change your password immediately.`,
                    userType: "STAFF",
                    specificUserIds: [newUserId],
                  };

                  const emailFormData = new FormData();
                  emailFormData.append('request', new Blob([JSON.stringify(emailData)], { type: 'application/json' }));

                  await axios.post(configData.SEND_EMAIL_NOTIFICATION, emailFormData, {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      'Content-Type': 'multipart/form-data',
                    },
                  });
                }
              } catch (emailError) {
                console.error(`Failed to send welcome email to ${email}:`, emailError);
              }
            } else {
              throw new Error(`Unexpected status ${response.status}`);
            }
          } catch (err) {
            console.error(`API Error for ${email}:`, err);
            stats.failed++;
            stats.errors.push({
              row: originalRow,
              email: email,
              reason: err.response?.data?.message || err.message || "Backend creation failed"
            });
          }
        }

        setImportedStaff(registeredStaff);
        setImportSummary(stats);
        setUploadStatus('complete');
        setIsProcessing(false);

        if (stats.success > 0) {
            toast.success(`Successfully imported ${stats.success} staff member(s).`);
        }
        if (stats.failed > 0) {
            toast.error(`Failed to import ${stats.failed} staff member(s). Check errors below.`);
        }

      } catch (error) {
        console.error('Bulk import error:', error);
        toast.error(`Failed to import staff: ${error.message}`);
        setUploadStatus('error');
        setIsProcessing(false);
      }
    };

    fileReader.onerror = () => {
      toast.error('Error reading file.');
      setUploadStatus('error');
      setIsProcessing(false);
    };

    fileReader.readAsText(file);
  };


  const handleViewStaff = () => {
    navigate('/faculty', { state: { orgId } });
  };

  const handleBack = () => {
    navigate('/faculty');
  };

  const generateCSVTemplate = () => {
    const headers =
      'firstName,lastName,email,phoneNumber,gender,dob,placeOfBirth,bloodGroup,nationality,religion,category,currentAddress,currentAddress2,city,state,pincode,alternatePhoneNumber,employeeId,designation,department,joiningDate,staffContractType,responsibilities,identificationMark,drivingLicenseNumber,drivingLicenseValidity,drivingExperienceYears,lastFourDigitsOfAadhaarNumber';
    const sampleRow1 =
      'Ramesh,Kumar,ramesh.kumar@example.com,9876543201,MALE,1980-03-10,Delhi,A_POSITIVE,Indian,Hindu,General,123 Street,,Delhi,Delhi,110001,9876543211,EMP2001,Driver,Transport,2020-01-15,PERMANENT,Driving;Maintenance,Mole on left cheek,DL123456,2025-01-15,10,1234';
    const sampleRow2 =
      'Sita,Devi,sita.devi@example.com,9876543202,FEMALE,1985-05-15,Mumbai,B_POSITIVE,Indian,Hindu,General,456 Street,Apt 12,Mumbai,Maharashtra,400001,,EMP2002,Clerk,Administration,2021-06-01,CONTRACT,Record Keeping;Filing,Scar on right arm,,,0,5678';

    return `${headers}\n${sampleRow1}\n${sampleRow2}`;
  };

  return (
    <div className="import-container">
      <div className="import-card">
        <div className="header-section">
          <button className="back-button" onClick={handleBack}>
            ← Back
          </button>
          <h2 className="title">Bulk Staff Import</h2>
        </div>

        <div className="content-section">
          <div className="upload-area">
            <label className="file-input-label">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden-input"
              />
              <span className="upload-button">📁 Choose CSV File</span>
            </label>
            {file && <p className="file-name">Selected: {file.name}</p>}
          </div>



          <button
            className={`import-button ${uploadStatus === 'uploading' ? 'loading' : ''}`}
            onClick={handleUpload}
            disabled={!file || uploadStatus === 'uploading'}
          >
            {uploadStatus === 'uploading' ? (
              <span className="spinner"></span>
            ) : (
              'Start Import'
            )}
          </button>

          {uploadStatus === 'complete' && importSummary && (
            <div className="summary-section">
              <div className="summary-card">
                <h3>Import Summary</h3>
                <div className="summary-stats">
                  <div className="stat-item warning">
                    <span className="count">{importSummary.duplicates}</span>
                    <span className="label">Duplicates (Skipped)</span>
                  </div>
                  <div className="stat-item error">
                    <span className="count">{importSummary.failed}</span>
                    <span className="label">Failed</span>
                  </div>
                </div>

                {importSummary.errors.length > 0 && (
                  <div className="error-details">
                    <h4>Error Details:</h4>
                    <ul>
                      {importSummary.errors.map((err, idx) => (
                        <li key={idx}>
                          <strong>Row {err.row} ({err.email}):</strong> {err.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <button
                  className="view-button"
                  onClick={handleViewStaff}
                  style={{ marginTop: '20px' }}
                >
                  Done & View List
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="csv-instructions">
          <h3>CSV Format Requirements</h3>
          <p>
            Download
            <a
              href={`data:text/csv;charset=utf-8,${encodeURIComponent(generateCSVTemplate())}`}
              download="StaffTemplate.csv"
            >
              sample CSV template
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default StaffBulkImport;